import type { DrillKeyframe } from '@4cm/core/drills/types';
import type { DrillPlaybackHook } from '../../lib/drillPlayback';
import { glassSurface } from './liquidGlass';
import { HudChip } from '../tacticsHud/HudChip';
import '../tacticsHud/tacticsHud.css';

interface DrillAnimationBarProps {
  playback: DrillPlaybackHook;
  keyframes: DrillKeyframe[];
  /** Capture a new keyframe from the current canvas state. */
  onCapture: () => void;
  /** Remove the keyframe with this id. */
  onRemove: (id: string) => void;
  /** Rename a keyframe inline. */
  onRename: (id: string, label: string) => void;
  /** Update a keyframe's duration (seconds). */
  onSetDuration: (id: string, sec: number) => void;
}

const SPEEDS = [0.5, 1, 2] as const;

/**
 * Full-width animation bar that sits below the pitch. Capture / scrub /
 * play / speed / loop + an editable keyframe strip.
 *
 *   - When < 2 keyframes: shows the "Capture first keyframe" prompt
 *   - When ≥ 2: shows the scrubber + play + speed + loop + keyframe chips
 *   - Each chip: click to jump, double-click to rename, × to remove
 */
export function DrillAnimationBar({
  playback, keyframes, onCapture, onRemove, onRename, onSetDuration,
}: DrillAnimationBarProps): React.JSX.Element {
  const { state, play, pause, reset, setT, setSpeed, setLoop } = playback;
  const isPlaying = state.mode === 'playing';
  const hasAnimation = keyframes.length >= 2;

  if (!hasAnimation) {
    return (
      <div style={wrap} aria-label="Animation timeline" data-testid="drill-animation-bar">
        <div style={controlsRow}>
          <button type="button" onClick={onCapture} style={captureBtn} data-testid="capture-keyframe">
            📷 Capture first keyframe
          </button>
          <details style={tipDetails} data-testid="animation-tip">
            <summary style={tipSummary} title="How animations work">ℹ How animations work</summary>
            <div style={tipBody}>
              A <strong>keyframe</strong> is a moment in the play. Capture the
              starting positions, move tokens to where they go next, capture
              again. Each keyframe in the timeline is a stop point; the
              animation plays smoothly between them. Adjust the per-keyframe
              duration to slow a phase down or speed it up. Hit ▶ Play to see
              the result.
            </div>
          </details>
        </div>
        <span style={hintStyle}>
          {keyframes.length === 0
            ? 'Capture multiple keyframes to build an animated play.'
            : '1 keyframe captured — move tokens + capture another to animate.'}
        </span>
      </div>
    );
  }

  return (
    <div style={wrap} aria-label="Animation timeline" data-testid="drill-animation-bar">
      <div style={controlsRow}>
        <button type="button" onClick={onCapture} style={captureBtn} data-testid="capture-keyframe">
          📷 Capture
        </button>
        <HudChip
          active={isPlaying}
          onClick={() => (isPlaying ? pause() : play())}
          title={isPlaying ? 'Pause playback' : 'Play the animation'}
          testId="play-toggle"
        >
          {isPlaying ? '⏸ Pause' : '▶ Play'}
        </HudChip>
        <HudChip
          onClick={reset}
          title="Reset playback to the start"
          testId="reset"
          disabled={state.t === 0 && state.mode === 'idle'}
        >
          ↺ Reset
        </HudChip>
        <div style={speedGroup}>
          {SPEEDS.map((s) => (
            <HudChip
              key={s}
              active={state.speed === s}
              onClick={() => setSpeed(s)}
              title={`Play at ${s}× speed`}
              testId={`speed-${s}`}
            >
              {s}×
            </HudChip>
          ))}
        </div>
        <HudChip
          active={state.loop}
          onClick={() => setLoop(!state.loop)}
          title="Loop the animation"
          testId="loop-toggle"
        >
          🔁 Loop
        </HudChip>
        <input
          type="range"
          min={0} max={1} step={0.001}
          value={state.t}
          onChange={(e) => setT(Number(e.target.value))}
          aria-label="Scrub timeline"
          data-testid="scrubber"
          style={{ flex: 1, marginLeft: 8 }}
        />
        <span style={timeStyle} data-testid="time-display">
          {formatTime(state.t * totalDuration(keyframes))} / {formatTime(totalDuration(keyframes))}
        </span>
      </div>

      <ol style={chipsRow} aria-label="Keyframes">
        {keyframes.map((k, idx) => (
          <KeyframeChip
            key={k.id}
            kf={k}
            index={idx}
            isFirst={idx === 0}
            playback={playback}
            keyframes={keyframes}
            onRemove={() => onRemove(k.id)}
            onRename={(label) => onRename(k.id, label)}
            onSetDuration={(sec) => onSetDuration(k.id, sec)}
          />
        ))}
      </ol>
    </div>
  );
}

interface KeyframeChipProps {
  kf: DrillKeyframe;
  index: number;
  isFirst: boolean;
  playback: DrillPlaybackHook;
  keyframes: DrillKeyframe[];
  onRemove: () => void;
  onRename: (label: string) => void;
  onSetDuration: (sec: number) => void;
}

function KeyframeChip({
  kf, index, isFirst, playback, keyframes, onRemove, onRename, onSetDuration,
}: KeyframeChipProps): React.JSX.Element {
  const total = totalDuration(keyframes);
  const tAtThisKf = total > 0 ? cumulativeTime(keyframes, index) / total : 0;
  const handleJump = () => playback.setT(tAtThisKf);
  return (
    <li style={chipWrap} data-testid={`kf-chip-${index}`}>
      <button type="button" onClick={handleJump} style={chipJump} title={`Jump to ${kf.label ?? `kf ${index + 1}`}`}>
        <span style={chipIdx}>{index + 1}</span>
        <input
          type="text"
          value={kf.label ?? `Phase ${index + 1}`}
          onChange={(e) => onRename(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Keyframe ${index + 1} label`}
          style={chipLabel}
        />
      </button>
      {!isFirst && (
        <label style={chipDuration}>
          <input
            type="number"
            value={Math.round((kf.durationSec ?? 1) * 10) / 10}
            min={0.1} step={0.1}
            onChange={(e) => onSetDuration(Number(e.target.value))}
            style={chipDurationInput}
            aria-label={`Keyframe ${index + 1} duration in seconds`}
          />
          <span style={{ fontSize: 9, opacity: 0.6 }}>s</span>
        </label>
      )}
      <button type="button" onClick={onRemove} style={chipRemove} title={`Remove ${kf.label ?? `kf ${index + 1}`}`}>×</button>
    </li>
  );
}

// ---- helpers --------------------------------------------------------------
function totalDuration(kfs: DrillKeyframe[]): number {
  if (kfs.length < 2) return 0;
  let s = 0;
  for (let i = 1; i < kfs.length; i += 1) s += Math.max(0.1, kfs[i].durationSec ?? 1);
  return s;
}
function cumulativeTime(kfs: DrillKeyframe[], upToIdx: number): number {
  let s = 0;
  for (let i = 1; i <= upToIdx; i += 1) s += Math.max(0.1, kfs[i].durationSec ?? 1);
  return s;
}
function formatTime(sec: number): string {
  if (!Number.isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ---- styles ---------------------------------------------------------------
const wrap: React.CSSProperties = {
  ...glassSurface,
  padding: 10,
  display: 'flex', flexDirection: 'column', gap: 10,
  fontFamily: 'system-ui, sans-serif',
};
const controlsRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
};
const speedGroup: React.CSSProperties = {
  display: 'flex', gap: 2,
};
const captureBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'rgba(39,174,96,0.2)',
  color: '#27ae60',
  border: '1px solid rgba(39,174,96,0.5)',
  borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};
const hintStyle: React.CSSProperties = {
  fontSize: 11, opacity: 0.7, fontStyle: 'italic',
};
const tipDetails: React.CSSProperties = {
  fontSize: 11,
  marginLeft: 'auto',
};
const tipSummary: React.CSSProperties = {
  cursor: 'help',
  listStyle: 'none',
  padding: '4px 10px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'rgba(255,255,255,0.78)',
};
const tipBody: React.CSSProperties = {
  marginTop: 8,
  padding: '10px 12px',
  background: 'rgba(15, 25, 45, 0.65)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.5,
  opacity: 0.85,
  maxWidth: 380,
};
const timeStyle: React.CSSProperties = {
  fontSize: 11, fontFamily: 'ui-monospace, monospace',
  opacity: 0.75, minWidth: 70, textAlign: 'right',
};
const chipsRow: React.CSSProperties = {
  listStyle: 'none', margin: 0, padding: 0,
  display: 'flex', gap: 6, flexWrap: 'wrap',
};
const chipWrap: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 0,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 4, overflow: 'hidden',
  fontSize: 11,
};
const chipJump: React.CSSProperties = {
  background: 'transparent', color: '#fff', border: 'none',
  padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 4,
  cursor: 'pointer', fontFamily: 'inherit',
};
const chipIdx: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  padding: '1px 5px', borderRadius: 3, fontWeight: 700, fontSize: 10,
};
const chipLabel: React.CSSProperties = {
  background: 'transparent', color: '#fff', border: 'none', outline: 'none',
  font: 'inherit', width: 76, padding: '2px 4px',
};
const chipDuration: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 1,
  borderLeft: '1px solid rgba(255,255,255,0.08)',
  padding: '0 4px',
};
const chipDurationInput: React.CSSProperties = {
  width: 30, background: 'transparent', color: '#fff',
  border: 'none', font: 'inherit', textAlign: 'right',
  padding: '2px 0',
};
const chipRemove: React.CSSProperties = {
  background: 'transparent', color: 'rgba(255,255,255,0.5)',
  border: 'none', borderLeft: '1px solid rgba(255,255,255,0.1)',
  padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
};
